import {apiFetch} from '../../../security/apiFetch';
import React, { useState, useEffect, useRef } from 'react';
import { Tooltip, Box, Typography, Chip, IconButton, Card, CardContent } from '@mui/material';
import { VolumeUp, Star, StarBorder, Close } from '@mui/icons-material';
import { playAudio } from '../../../utils/audioUtils';

interface TermTooltipProps {
  children: React.ReactNode;
  termId: string;
  termText: string;
  token: string | null;
  position?: { x: number; y: number };
  onClose?: () => void;
}

interface TermData {
  id: string;
  term_arabic: string;
  term_transliteration: string;
  term_transliteration_cyr: string;
  definition_short: string;
  definition_full: string;
  category: string;
  audio_url?: string;
  is_favorite?: boolean;
}

const TermTooltip: React.FC<TermTooltipProps> = ({ 
  children, 
  termId, 
  termText, 
  token, 
  position,
  onClose 
}) => {
  const [termData, setTermData] = useState<TermData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [open, setOpen] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Загружаем данные термина
  useEffect(() => {
    const loadTermData = async () => {
      if (!termId) return;
      
      setIsLoading(true);
      try {
        const response = await apiFetch(`/api/v1/knowledge/entries/${termId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        
        if (response.ok) {
          const data = await response.json();
          setTermData(data);
          setIsFavorite(data.is_favorite || false);
        }
      } catch (error) {
        console.error('Ошибка загрузки термина:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadTermData();
  }, [termId, token]);

  // Обработка клика по элементу
  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen(true);
  };

  // Воспроизведение аудио
  const handlePlayAudio = async () => {
    if (!termData?.audio_url) return;
    
    try {
      setIsPlaying(true);
      await playAudio(termData.audio_url, token);
    } catch (error) {
      console.error('Ошибка воспроизведения аудио:', error);
    } finally {
      setIsPlaying(false);
    }
  };

  // Переключение избранного
  const handleToggleFavorite = async () => {
    if (!termData) return;
    
    try {
      const method = isFavorite ? 'DELETE' : 'POST';
      const response = await apiFetch(`/api/v1/user-terms/favorites/${termData.id}`, {
        method,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        setIsFavorite(!isFavorite);
      }
    } catch (error) {
      console.error('Ошибка изменения избранного:', error);
    }
  };

  // Закрытие тултипа
  const handleClose = () => {
    setOpen(false);
    onClose?.();
  };

  // Получение цвета категории
  const getCategoryColor = (category: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    const colors: { [key: string]: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" } = {
      'tajweed': 'primary',
      'fiqh': 'secondary',
      'aqeedah': 'error',
      'grammar': 'info',
      'general': 'default'
    };
    return colors[category] || 'default';
  };

  // Получение названия категории
  const getCategoryLabel = (category: string): string => {
    const labels: { [key: string]: string } = {
      'tajweed': 'Таджвид',
      'fiqh': 'Фикх',
      'aqeedah': 'Акыда',
      'grammar': 'Грамматика',
      'general': 'Общее'
    };
    return labels[category] || category;
  };

  // Кастомный контент тултипа
  const tooltipContent = (
    <Card 
      ref={tooltipRef}
      sx={{ 
        maxWidth: 400, 
        minWidth: 300,
        position: 'relative',
        boxShadow: 3
      }}
    >
      <CardContent sx={{ p: 2 }}>
        {/* Заголовок с кнопкой закрытия */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontFamily: 'Noto Naskh Arabic, serif' }}>
            {termData?.term_arabic || termText}
          </Typography>
          <IconButton size="small" onClick={handleClose}>
            <Close fontSize="small" />
          </IconButton>
        </Box>

        {isLoading ? (
          <Typography variant="body2" color="text.secondary">
            Загружаем информацию о термине...
          </Typography>
        ) : termData ? (
          <>
            {/* Транскрипция */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {termData.term_transliteration} ({termData.term_transliteration_cyr})
            </Typography>

            {/* Категория */}
            <Box sx={{ mb: 2 }}>
              <Chip 
                label={getCategoryLabel(termData.category)} 
                color={getCategoryColor(termData.category)}
                size="small"
              />
            </Box>

            {/* Краткое определение */}
            <Typography variant="body2" sx={{ mb: 2 }}>
              {termData.definition_short}
            </Typography>

            {/* Полное определение (если отличается от краткого) */}
            {termData.definition_full && termData.definition_full !== termData.definition_short && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {termData.definition_full}
              </Typography>
            )}

            {/* Действия */}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              {termData.audio_url && (
                <IconButton
                  size="small"
                  onClick={handlePlayAudio}
                  disabled={isPlaying}
                  title="Прослушать произношение"
                >
                  <VolumeUp fontSize="small" />
                </IconButton>
              )}
              
              <IconButton
                size="small"
                onClick={handleToggleFavorite}
                title={isFavorite ? "Убрать из избранного" : "Добавить в избранное"}
              >
                {isFavorite ? <Star fontSize="small" color="warning" /> : <StarBorder fontSize="small" />}
              </IconButton>
            </Box>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Термин не найден
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Tooltip
      open={open}
      title={tooltipContent}
      placement="top"
      arrow
      disableFocusListener
      disableHoverListener
      disableTouchListener
      PopperProps={{
        anchorEl: position ? { 
          getBoundingClientRect: () => ({
            top: position.y,
            left: position.x,
            right: position.x,
            bottom: position.y,
            width: 0,
            height: 0,
            x: position.x,
            y: position.y,
            toJSON: () => ({})
          })
        } : undefined,
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 10],
            },
          },
        ],
      }}
    >
      <Box component="span" onClick={handleClick} sx={{ cursor: 'pointer' }}>
        {children}
      </Box>
    </Tooltip>
  );
};

export default TermTooltip;
